import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Straighten as StraightenIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-admin';
import type { SizePropertyInput, Partner } from '@src/types';
import * as RewareApi from '@src/apis/reware';

const steps = ['Size Identity', 'Size Groups', 'Size Synonyms', 'Size Components', 'Review'];

export const AddSizeCodeMapping: React.FC = () => {
  const navigate = useNavigate();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<SizePropertyInput>({
    code: '',
    display: '',
    synonyms: [],
    family: [],
    primary_value: '',
    primary_label: '',
    secondary_value: '',
    secondary_label: '',
    tertiary_value: '',
    tertiary_label: '',
  });

  const [newSynonym, setNewSynonym] = useState('');
  const [newFamily, setNewFamily] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleBackToList = () => {
    navigate('/catalog/attributes?tab=sizes');
  };

  // Check for duplicate size code
  useEffect(() => {
    const checkDuplicate = async () => {
      if (!formData.code || !currentPartner) {
        setIsDuplicate(false);
        return;
      }

      setCheckingDuplicate(true);
      try {
        const response = await RewareApi.getSizeProperties({
          partnerCode: currentPartner.code,
          filter: { searchQuery: formData.code },
          page: 1,
          perPage: 100,
        });

        // Check if exact match exists
        const exactMatch = response.data.find(
          prop => prop.code.toLowerCase() === formData.code.toLowerCase()
        );
        setIsDuplicate(!!exactMatch);
      } catch (error) {
        console.error('Failed to check for duplicate:', error);
      } finally {
        setCheckingDuplicate(false);
      }
    };

    // Debounce the check
    const timeoutId = setTimeout(checkDuplicate, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.code, currentPartner]);

  const handleSave = async () => {
    if (!currentPartner) {
      setSaveError('No partner selected. Please refresh and try again.');
      return;
    }

    if (isDuplicate) {
      setSaveError(`Size code "${formData.code}" already exists. Please use a different code or edit the existing mapping.`);
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      const token = localStorage.getItem('token') || '';

      await RewareApi.createSizeProperty({
        partnerCode: currentPartner.code,
        token,
        data: formData,
      });

      // Navigate back to size tab on success
      void navigate('/catalog/attributes?tab=sizes');
    } catch (error) {
      console.error('Failed to save size property:', error);

      // Extract error message from various error formats
      let errorMessage = 'Failed to save size mapping. Please try again.';

      if (error?.response?.data?.errors?.[0]?.message) {
        errorMessage = error.response.data.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Check for common error types
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        errorMessage = `Size code "${formData.code}" already exists. Please use a different code.`;
      }

      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof SizePropertyInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addSynonym = () => {
    if (newSynonym.trim()) {
      updateField('synonyms', [...(formData.synonyms || []), newSynonym.trim()]);
      setNewSynonym('');
    }
  };

  const removeSynonym = (index: number) => {
    const newSynonyms = [...(formData.synonyms || [])];
    newSynonyms.splice(index, 1);
    updateField('synonyms', newSynonyms);
  };

  const addFamily = () => {
    if (newFamily.trim()) {
      updateField('family', [...(formData.family || []), newFamily.trim()]);
      setNewFamily('');
    }
  };

  const removeFamily = (index: number) => {
    const newFamilies = [...(formData.family || [])];
    newFamilies.splice(index, 1);
    updateField('family', newFamilies);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Size Identity
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BadgeIcon /> Size Identity
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              The catalog code and the customer-facing size name it maps to.
            </Typography>

            <TextField
              label="Size Code"
              required
              fullWidth
              value={formData.code}
              onChange={(e) => updateField('code', e.target.value)}
              error={isDuplicate}
              helperText={
                isDuplicate ?
                  `Code "${formData.code}" already exists` :
                  checkingDuplicate ?
                    'Checking for duplicates...' :
                    'The code used in your catalog data (e.g., "34-30", "MD")'
              }
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: checkingDuplicate ? (
                  <CircularProgress size={20} />
                ) : isDuplicate ? (
                  <WarningIcon color="error" />
                ) : null,
              }}
            />

            {isDuplicate && (
              <Alert severity="error" sx={{ mb: 3 }}>
                This size code already exists. Please choose a different code, or go back to the list and edit the existing mapping.
              </Alert>
            )}

            <TextField
              label="Size Display Name"
              required
              fullWidth
              value={formData.display}
              onChange={(e) => updateField('display', e.target.value)}
              helperText='Customer-facing size name (e.g., "W32 L30", "Medium")'
            />
          </Box>
        );

      case 1: // Size Groups
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StraightenIcon /> Size Groups
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Groups drive storefront size filtering. A size can belong to multiple groups (e.g., XL & Regular).
            </Typography>

            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Size Groups
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Used for storefront filtering. A size can belong to multiple groups (e.g., XL & Regular).
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.family?.map((group, index) => (
                  <Chip
                    key={index}
                    label={group}
                    onDelete={() => removeFamily(index)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add size group"
                  value={newFamily}
                  onChange={(e) => setNewFamily(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addFamily()}
                  fullWidth
                />
                <Button onClick={addFamily} variant="outlined">
                  Add
                </Button>
              </Box>
            </Card>
          </Box>
        );

      case 2: // Size Synonyms
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SwapHorizIcon /> Size Synonyms
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Other size codes (e.g., from garment tags or supplier feeds) that should map to this size.
            </Typography>

            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Size Synonyms
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Any catalog record using one of these codes will resolve to this size mapping.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {formData.synonyms?.map((synonym, index) => (
                  <Chip
                    key={index}
                    label={synonym}
                    onDelete={() => removeSynonym(index)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder="Add synonym"
                  value={newSynonym}
                  onChange={(e) => setNewSynonym(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSynonym()}
                  fullWidth
                />
                <Button onClick={addSynonym} variant="outlined">
                  Add
                </Button>
              </Box>
            </Card>
          </Box>
        );

      case 3: // Size Components
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewColumnIcon /> Size Components
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              For sizes made of multiple measurements, define each component so shoppers can select them separately on the product page. All components are optional.
            </Typography>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              PRIMARY COMPONENT
              <Chip label="Optional" size="small" sx={{ ml: 1 }} />
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Value"
                  fullWidth
                  value={formData.primary_value}
                  onChange={(e) => updateField('primary_value', e.target.value)}
                  placeholder='e.g., "34"'
                  helperText='e.g., "34"'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Label"
                  fullWidth
                  value={formData.primary_label}
                  onChange={(e) => updateField('primary_label', e.target.value)}
                  placeholder='e.g., "waist"'
                  helperText='e.g., "waist"'
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              SECONDARY COMPONENT
              <Chip label="Optional" size="small" sx={{ ml: 1 }} />
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Value"
                  fullWidth
                  value={formData.secondary_value}
                  onChange={(e) => updateField('secondary_value', e.target.value)}
                  placeholder='e.g., "inseam"'
                  helperText='e.g., "30"'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Label"
                  fullWidth
                  value={formData.secondary_label}
                  onChange={(e) => updateField('secondary_label', e.target.value)}
                  placeholder='e.g., "inseam"'
                  helperText='e.g., "inseam"'
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
              TERTIARY COMPONENT
              <Chip label="Optional" size="small" sx={{ ml: 1 }} />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display Value"
                  fullWidth
                  value={formData.tertiary_value}
                  onChange={(e) => updateField('tertiary_value', e.target.value)}
                  placeholder='e.g., "tall"'
                  helperText='e.g., "tall"'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Label"
                  fullWidth
                  value={formData.tertiary_label}
                  onChange={(e) => updateField('tertiary_label', e.target.value)}
                  placeholder='e.g., "Length"'
                  helperText='e.g., "Length"'
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 4: // Review
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StraightenIcon /> Review
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Confirm the mapping before saving.
            </Typography>

            {saveError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            )}

            {isDuplicate && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Size code &quot;
                {formData.code}
                &quot; already exists. Please go back and choose a different code.
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Size Code
                    </Typography>
                    <Typography variant="body1">{formData.code || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Size Display Name
                    </Typography>
                    <Typography variant="body1">{formData.display || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Size Groups
                    </Typography>
                    <Typography variant="body1">{formData.family?.join(', ') || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Size Synonyms
                    </Typography>
                    <Typography variant="body1">{formData.synonyms?.join(', ') || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Primary Component
                    </Typography>
                    <Typography variant="body1">
                      {formData.primary_value && formData.primary_label
                        ? `${formData.primary_value} (${formData.primary_label})`
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Secondary Component
                    </Typography>
                    <Typography variant="body1">
                      {formData.secondary_value && formData.secondary_label
                        ? `${formData.secondary_value} (${formData.secondary_label})`
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tertiary Component
                    </Typography>
                    <Typography variant="body1">
                      {formData.tertiary_value && formData.tertiary_label
                        ? `${formData.tertiary_value} (${formData.tertiary_label})`
                        : '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepComplete = () => {
    switch (activeStep) {
      case 0: // Size Identity
        return formData.code && formData.display && !isDuplicate && !checkingDuplicate;
      case 1: // Size Groups
        return true; // Optional
      case 2: // Size Synonyms
        return true; // Optional
      case 3: // Size Components
        return true; // All optional
      case 4: // Review
        return !isDuplicate && !saving; // Can't save if duplicate or already saving
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StraightenIcon /> Add Size Code Mapping
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToList}
        >
          Back to Code Mappings
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Map a catalog code to its display name, grouping, and storefront presentation
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={activeStep === 0 ? handleBackToList : handleBack}>
              {activeStep === 0 ? '← Back' : 'Back'}
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isStepComplete()}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {saving ? 'Saving...' : 'Save Size Code Mapping'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepComplete()}
              >
                Next →
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
